import { useState, useRef, useEffect, type CSSProperties, type Ref } from "react";
import type { ValidationStatus } from "../types";
import { teamById, teamLabel, DEFAULT_TEAM } from "../teams";
import { Icons } from "./icons";

const MONTHS_ABBR = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const WEEKDAYS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];

function parse(v: string) {
  const [y, m, d] = v.split("-").map(Number);
  return new Date(y, m - 1, d);
}

const LIT: Record<string, string> = {
  vert: "var(--lit-vert-fg)",
  violet: "var(--lit-violet-fg)",
  blanc: "var(--lit-blanc-fg)",
  rouge: "var(--lit-rouge-fg)",
  rose: "var(--lit-rose-fg)",
};

export type RailStatus = "archive" | "draft" | "empty";
export interface RailItem {
  date: string;
  dayName: string;
  color?: string;
  status: RailStatus;
  validation?: ValidationStatus; // priest sign-off state, for upcoming drafts
  by?: string;
  team?: string; // animation team id responsible for this Sunday
  viaTool?: boolean; // archives: prepared with the app (vs. outside it / before it)
  removable?: boolean; // only team-plan Sundays can be removed (not historical archives)
}

export function SundayRail({
  items = [],
  value,
  onSelect,
  onRemove,
  style,
}: {
  items: RailItem[];
  value: string;
  onSelect?: (date: string) => void;
  onRemove?: (date: string) => void;
  style?: CSSProperties;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLDivElement>(null);
  // Which edges still have hidden cards — drives the fades, so a card resting at
  // the start/end (or all cards when they fit) is never covered by a fade.
  const [edges, setEdges] = useState({ start: false, end: false });

  function updateEdges() {
    const sc = scrollerRef.current;
    if (!sc) return;
    const max = sc.scrollWidth - sc.clientWidth;
    const start = sc.scrollLeft > 1;
    const end = sc.scrollLeft < max - 1;
    setEdges((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }

  // Keep the selected card visible (centered) when selection or the list changes.
  useEffect(() => {
    const sc = scrollerRef.current;
    const el = selectedRef.current;
    if (!sc) return;
    if (el) {
      const target = el.offsetLeft - (sc.clientWidth - el.clientWidth) / 2;
      sc.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
    }
    updateEdges();
    const t = window.setTimeout(updateEdges, 380); // recompute after the smooth scroll settles
    return () => window.clearTimeout(t);
  }, [value, items.length]);

  useEffect(() => {
    updateEdges();
    window.addEventListener("resize", updateEdges);
    return () => window.removeEventListener("resize", updateEdges);
  }, []);

  return (
    <div style={{ position: "relative", ...style }}>
      {/* Generous padding so the selected border, hover shadow and × button are
          never clipped by this horizontal scroller's overflow. scrollPaddingInline
          keeps snapped cards aligned with that gutter. */}
      <div ref={scrollerRef} onScroll={updateEdges} style={{ display: "flex", gap: "10px", overflowX: "auto", scrollSnapType: "x proximity", scrollPaddingInline: 20, padding: "12px 20px 16px", scrollbarWidth: "thin" }}>
        {items.map((it) => (
          <CardWrap key={it.date} item={it} selected={it.date === value} onSelect={onSelect} onRemove={onRemove} cardRef={it.date === value ? selectedRef : undefined} />
        ))}
      </div>
      {edges.start && (
        <div aria-hidden style={{ position: "absolute", top: 0, bottom: 16, left: 0, width: 24, pointerEvents: "none", background: "linear-gradient(90deg, var(--bg-app), transparent)" }} />
      )}
      {edges.end && (
        <div aria-hidden style={{ position: "absolute", top: 0, bottom: 16, right: 0, width: 24, pointerEvents: "none", background: "linear-gradient(270deg, var(--bg-app), transparent)" }} />
      )}
    </div>
  );
}

function StatusPill({ item }: { item: RailItem }) {
  // For upcoming drafts, the validation state takes precedence over the plain "Brouillon".
  const kind: RailStatus | "a_valider" | "valide" =
    item.status === "archive" || item.status === "empty"
      ? item.status
      : item.validation === "valide"
        ? "valide"
        : item.validation === "a_valider"
          ? "a_valider"
          : "draft";
  const map: Record<string, { t: string; fg: string; bg: string; ring: string; check?: boolean }> = {
    archive: { t: "Archivé", fg: "var(--lit-vert-fg)", bg: "var(--lit-vert-bg)", ring: "var(--lit-vert-ring)", check: true },
    valide: { t: "Validé", fg: "var(--lit-vert-fg)", bg: "var(--lit-vert-bg)", ring: "var(--lit-vert-ring)", check: true },
    a_valider: { t: "À valider", fg: "var(--sacred-text)", bg: "var(--sacred-soft)", ring: "var(--sacred-ring)" },
    draft: { t: "Brouillon", fg: "var(--accent-text)", bg: "var(--accent-soft)", ring: "var(--accent-ring)" },
    empty: { t: "À préparer", fg: "var(--text-muted)", bg: "var(--bg-sunken)", ring: "var(--border)" },
  };
  const s = map[kind] || map.empty;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", fontWeight: 600, color: s.fg, background: s.bg, border: `1px solid ${s.ring}`, borderRadius: "var(--radius-pill)", padding: "1px 8px", whiteSpace: "nowrap" }}>
      {s.check && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>}
      {s.t}
    </span>
  );
}

// Archives: was the mass prepared with the app? Shown on the card so the team can look back.
function ToolLine({ item }: { item: RailItem }) {
  const label = item.viaTool ? `Avec l'outil${item.by ? ` · ${item.by}` : ""}` : "Sans l'outil";
  return (
    <span
      title={item.viaTool ? `Préparée avec l'outil${item.by ? ` (dernière modification : ${item.by})` : ""}` : "Préparée sans l'outil"}
      style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 1, minWidth: 0, fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", fontWeight: item.viaTool ? 600 : 400, color: item.viaTool ? "var(--accent-text)" : "var(--text-faint)" }}
    >
      {item.viaTool ? (
        <Icons.Sparkle size={11} stroke={2.6} style={{ flexShrink: 0 }} />
      ) : (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" style={{ flexShrink: 0 }}><path d="M6 12h12" /></svg>
      )}
      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
    </span>
  );
}

function caption(item: RailItem): string {
  if (item.status === "empty") return "Aucune préparation";
  if (item.validation === "valide") return item.by ? `Validé · ${item.by}` : "Validé par le Père";
  if (item.validation === "a_valider") return item.by ? `À valider · ${item.by}` : "En attente du Père";
  return "Modifié — enregistré";
}

function CardWrap({ item, selected, onSelect, onRemove, cardRef }: { item: RailItem; selected: boolean; onSelect?: (d: string) => void; onRemove?: (d: string) => void; cardRef?: Ref<HTMLDivElement> }) {
  const [hover, setHover] = useState(false);
  return (
    <div ref={cardRef} style={{ position: "relative", flex: "none" }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <SundayCard item={item} selected={selected} onSelect={onSelect} />
      {onRemove && item.removable && (hover || selected) && (
        <button
          type="button"
          aria-label="Retirer ce dimanche de la liste"
          title="Retirer de la liste"
          onClick={(e) => { e.stopPropagation(); onRemove(item.date); }}
          style={{ position: "absolute", top: -9, right: -9, width: 28, height: 28, borderRadius: "50%", border: "1px solid var(--border-strong)", background: "var(--bg-raised)", color: "var(--text-muted)", cursor: "pointer", boxShadow: "var(--shadow-sm)", display: "inline-flex", alignItems: "center", justifyContent: "center", zIndex: 3 }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      )}
    </div>
  );
}

function SundayCard({ item, selected, onSelect }: { item: RailItem; selected: boolean; onSelect?: (d: string) => void }) {
  const [hover, setHover] = useState(false);
  const d = parse(item.date);
  const dot = LIT[item.color || "vert"] || LIT.vert;
  const isArchive = item.status === "archive";
  // Upcoming masses fall back to the default team; archives show only a recorded one.
  const team = teamById(item.team) || (isArchive ? undefined : teamById(DEFAULT_TEAM));
  const border = selected ? "var(--accent)" : hover ? "var(--border-strong)" : "var(--border)";
  const bg = selected ? "var(--accent-soft)" : "var(--bg-raised)";

  return (
    <button
      type="button"
      onClick={() => onSelect && onSelect(item.date)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        flex: "none", width: 170, scrollSnapAlign: "start", textAlign: "left",
        display: "flex", flexDirection: "column", gap: 8, background: bg,
        border: `2px solid ${border}`, borderRadius: "var(--radius-lg)", overflow: "hidden", padding: "11px 12px 12px", cursor: "pointer",
        boxShadow: selected || hover ? "var(--shadow-sm)" : "none",
        outline: "none",
        transition: "border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out), background var(--dur-fast) var(--ease-out)",
        opacity: item.status === "empty" && !selected ? 0.82 : 1,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
        <span style={{ width: 9, height: 9, borderRadius: "50%", background: dot, flexShrink: 0 }} />
        <StatusPill item={item} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <span style={{ fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>{WEEKDAYS[d.getDay()]}</span>
        <span style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: "var(--text-lg)", lineHeight: 1.1, color: "var(--text-primary)" }}>
          {d.getDate()} {MONTHS_ABBR[d.getMonth()]}
        </span>
      </div>
      <span style={{ fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", color: "var(--text-secondary)", lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={item.dayName}>
        {item.dayName}
      </span>
      {isArchive ? (
        <ToolLine item={item} />
      ) : (
        <span style={{ marginTop: 1, fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", color: "var(--text-faint)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {caption(item)}
        </span>
      )}
      {team && (
        <>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 2, fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", fontWeight: 600, color: "var(--text-secondary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={teamLabel(team)}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: team.color, flexShrink: 0 }} />
            {teamLabel(team)}
          </span>
          <span aria-hidden style={{ marginTop: 7, marginLeft: -14, marginRight: -14, marginBottom: -14, height: 6, background: team.color }} />
        </>
      )}
    </button>
  );
}
